import React from 'react';
import { AlertCircle, X, RefreshCw } from 'lucide-react';
import { useVersionCheck } from '../hooks/useVersionCheck';

interface UpdateNotificationProps {
  variant?: 'banner' | 'modal' | 'toast' | 'footer';
  position?: 'top' | 'bottom';
  className?: string;
}

const UpdateNotification: React.FC<UpdateNotificationProps> = ({ 
  variant = 'banner', 
  position = 'top',
  className = ''
}) => {
  const { hasUpdate, currentVersion, latestVersion, handleUpdate, dismissUpdate } = useVersionCheck();

  if (!hasUpdate) return null;

  const baseClasses = "bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-primary shadow-lg";
  const positionClasses = position === 'top' ? "fixed top-4 left-4 right-4 z-50" : "fixed bottom-4 left-4 right-4 z-50";
  
  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-primary" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-gray-900">Yeni Güncelleme Mevcut!</h3>
            </div>
          </div>
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Uygulamanın yeni bir versiyonu yayınlandı. En iyi deneyim için güncelleyin.
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <div>Mevcut: {currentVersion}</div>
              <div>Yeni: {latestVersion}</div>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={dismissUpdate}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Daha Sonra
            </button>
            <button
              onClick={handleUpdate}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition-colors flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Güncelle
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'toast') {
    return (
      <div className={`${positionClasses} ${baseClasses} p-4 rounded-md ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-primary mr-3" />
            <div>
              <h3 className="text-sm font-medium text-primary-dark">Yeni Güncelleme!</h3>
              <p className="text-sm text-primary-dark mt-1">
                Uygulamanın yeni versiyonu mevcut.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleUpdate}
              className="bg-primary text-white px-3 py-1 rounded text-sm hover:bg-primary-dark transition-colors flex items-center"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Güncelle
            </button>
            <button
              onClick={dismissUpdate}
              className="text-primary hover:text-primary-dark"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-primary p-2 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0 flex-1">
            <AlertCircle className="w-4 h-4 text-primary mr-2 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <span className="text-xs font-medium text-primary-dark">Yeni Güncelleme Mevcut!</span>
              <span className="text-xs text-primary-dark ml-2">
                Uygulamanın yeni bir versiyonu yayınlandı. En iyi deneyim için güncelleyin.
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-2">
            <button
              onClick={dismissUpdate}
              className="text-xs text-primary hover:text-primary-dark px-2 py-1 rounded hover:bg-primary/20 transition-colors"
            >
              Daha Sonra
            </button>
            <button
              onClick={handleUpdate}
              className="bg-primary text-white px-2 py-1 rounded text-xs hover:bg-primary-dark transition-colors flex items-center"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Güncelle
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default banner variant
  return (
    <div className={`${positionClasses} ${baseClasses} p-3 sm:p-4 rounded-md ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start sm:items-center">
          <AlertCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-primary-dark">Yeni Güncelleme Mevcut!</h3>
            <p className="text-sm text-primary-dark mt-1 leading-relaxed">
              Uygulamanın yeni bir versiyonu yayınlandı. En iyi deneyim için güncelleyin.
            </p>
            <div className="text-xs text-primary mt-1">
              <span className="hidden sm:inline">Mevcut: {currentVersion} → Yeni: {latestVersion}</span>
              <span className="sm:hidden block">
                <div>Mevcut: {currentVersion}</div>
                <div>Yeni: {latestVersion}</div>
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:flex-shrink-0">
          <button
            onClick={dismissUpdate}
            className="text-primary hover:text-primary-dark text-sm px-2 py-1 rounded hover:bg-primary/20 transition-colors text-center"
          >
            Daha Sonra
          </button>
          <button
            onClick={handleUpdate}
            className="bg-primary text-white px-3 py-2 rounded text-sm hover:bg-primary-dark transition-colors flex items-center justify-center"
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            Güncelle
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;

